/*
 *   Copyright (c) 2026 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";
import { CostumePageService } from "../../costumePageService";
import { GET_MY_TICKETS } from "../graphql/getMyTickets.query";
import { IMyTicketOrder, ITicketOrder } from "../../../pages/Costume/components/SelectTicketsComp/types";
import { isSchemaMissingError } from "../../../helpers/ticketError.helper";
import { ITicket, TicketStatus } from "../../../common/tickets";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IGetMyTicketsMethods extends IPluginMethodMap {
  getMyTickets(): Promise<any>;
}

export class GetMyTicketsService implements IAgentPlugin {
  readonly methods: IGetMyTicketsMethods;

  // Falls back to the pre-existing box-ticket-orders query if the real
  // getMyTickets API ever rejects the request (e.g. a doc/schema mismatch),
  // so this page never hard-breaks the way the Events listing briefly did.
  private purchaseApiReady = true;

  constructor() {
    this.methods = {
      getMyTickets: this.getMyTickets.bind(this),
    };
  }

  private sortTickets(tickets: ITicket[]): ITicket[] {
    return [...tickets].sort(
      (a, b) =>
        new Date(a.eventStart).getTime() - new Date(b.eventStart).getTime()
    );
  }

  async getMyTickets() {
    if (this.purchaseApiReady) {
      try {
        const orders = await this.fetchRealMyTickets();
        return { data: this.sortTickets(orders.map((o) => this.toTicket(o))) };
      } catch (err: any) {
        if (!isSchemaMissingError(err)) throw err;
        console.warn(
          "GetMyTicketsService: getMyTickets not available yet, falling back to box ticket orders:",
          err?.message || err
        );
        this.purchaseApiReady = false;
      }
    }

    const orders = await this.fetchLegacyTicketOrders();
    return { data: this.sortTickets(orders.map((o) => this.toLegacyTicket(o))) };
  }

  private async fetchRealMyTickets(): Promise<IMyTicketOrder[]> {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule?.getClient("graphql-gateway");
    if (!client) return [];
    const res = await client.query({
      query: GET_MY_TICKETS,
      fetchPolicy: "network-only",
    });
    return res?.data?.getMyTickets || [];
  }

  // Pre-feat/ticket-purchase fallback: box (costume) ticket orders only -
  // there is no standalone-purchase order source without the real API.
  private async fetchLegacyTicketOrders(): Promise<ITicketOrder[]> {
    try {
      return await CostumePageService.getInstance().getMyTicketOrders();
    } catch (err) {
      console.warn("GetMyTicketsService legacy fallback error:", err);
      return [];
    }
  }

  private toStatus(status: string): TicketStatus {
    switch (String(status || "").toUpperCase()) {
      case "PAID":
        return "PAID";
      case "HELD":
      case "PAYING":
      case "PAID_SYNC_PENDING":
        return "HOLD";
      case "RELEASED":
      case "EXPIRED":
        return "CANCELLED";
      default:
        return "PENDING";
    }
  }

  // One ticket order becomes one card.
  private toTicket(order: IMyTicketOrder): ITicket {
    const lines = order.lines || [];
    const status = this.toStatus(order.status);
    const paid = status === "PAID";
    const tickets = order.tickets || [];
    const firstWithImage = tickets.find((t) => t.qrImage) || tickets[0];

    return {
      id: order.ticketOrderId,
      eventName: order.feteName || order.event?.name || "Event",
      eventStart:
        order.feteStartsAt ||
        order.event?.startDate ||
        new Date().toISOString(),
      venue: order.event?.venue || "",
      venueAddress: null,
      posterImage: order.event?.imageUrl || null,
      ticketType: lines.map((l) => l.tierName).join(", "),
      quantity: lines.reduce((sum, l) => sum + (l.quantity || 0), 0),
      unitPrice: order.total,
      total: order.total,
      currency: order.currency || "TTD",
      status,
      source: "STANDALONE",
      viewTicketsUrl: null,
      // qrImage is null if the organiser hid QR codes or event details
      // couldn't load; the UI falls back to a placeholder in that case.
      qrPayload: paid ? firstWithImage?.qrCode || order.ticketOrderId : null,
      qrImage: paid ? firstWithImage?.qrImage || null : null,
      holdExpiresAt: order.holdExpiresAt || null,
      ticketsIssued: paid ? tickets : null,
    };
  }

  // Same mapping as before this feature: tickets bought with a costume.
  private toLegacyTicket(order: ITicketOrder): ITicket {
    const lines = order.lines || [];
    const status = this.toStatus(order.status);
    const paid = status === "PAID";
    return {
      id: order.ticketOrderId,
      eventName: order.feteName || "Event",
      eventStart: order.feteStartsAt || order.createdAt || new Date().toISOString(),
      venue: "",
      venueAddress: null,
      posterImage: null,
      ticketType: lines.map((l) => l.tierName).join(", "),
      quantity: lines.reduce((sum, l) => sum + (l.quantity || 0), 0),
      unitPrice: lines.length === 1 ? lines[0].unitPrice : order.total,
      total: order.total,
      currency: order.currency || "TTD",
      status,
      source: order.boxId ? "WITH_COSTUME" : "STANDALONE",
      viewTicketsUrl: paid ? order.viewTicketsUrl || null : null,
      qrPayload: paid ? order.tickets?.[0]?.ticketId || null : null,
      holdExpiresAt: order.holdExpiresAt || null,
    };
  }
}
